/*
 * Copyright (c) 2024. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */

import React, {forwardRef} from "react";
import {useTranslation} from "react-i18next";
import DatePicker, {registerLocale, setDefaultLocale} from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {addMonths} from "date-fns";
import Dates from "utils/Dates";
import {DEFAULT_RESERVATION_DAYS_FROM_NOW, LOCALE} from "helpers/config";
import {useTypedSelector} from "helpers/reducers";
import {UseFormSetValue} from "react-hook-form/dist/types";
import {BookingFilter} from "types/app";
import {declareLocaleByConfig} from "utils/localeUtils";
import {Package} from "types/entities/Package";
import {
    packageCategoryExcludeDates,
    packageExcludeDates,
    packageMaximumDate,
    packageMinimalDate,
} from "utils/packageUtils";
import PackageMinDaysFromNow from "pages/Front/Booking/components/Packages/PackageMinDaysFromNow";
import clsx from "clsx";
import CardLabel from "pages/Front/Booking/components/BookingCustomer/CardLabel";
import {useHallOpening} from "utils/hooks/useHallOpening";
import {useHallOpeningHours} from "utils/hooks/useHallOpeningHours";
import {PackageCategory} from "types/enums/PackageCategory";

interface BookingDateFilterProps {
    setValue: UseFormSetValue<BookingFilter>;
    register: any;
    showTitle: boolean;
    onChange: (date: string) => void;
    package: Package | null;
    packageCategory?: PackageCategory | null;
    disabled?: boolean;
}

const BookingDateFilter = (props: BookingDateFilterProps) => {
    const {t} = useTranslation();
    const hall = useTypedSelector(state => state.hallState.hall);
    const hallOpening = useHallOpening();
    const {getExcludedDays} = useHallOpeningHours();
    // const minimalDatePreset =
    //     hallOpening && hallOpening.hallOpeningDefined && hallOpening.hallOpeningDate
    //         ? new Date(hallOpening.hallOpeningDate)
    //         : new Date(Date.now() + DEFAULT_RESERVATION_DAYS_FROM_NOW * 24 * 60 * 60 * 1000);
    const minimalDatePreset = new Date(Date.now() + DEFAULT_RESERVATION_DAYS_FROM_NOW * 24 * 60 * 60 * 1000);

    const startDate = useTypedSelector(state => state.bookingState.bookingProcess.filter.date);
    const selectedPackage: Package | null = props.package || useTypedSelector(state => state.bookingState.bookingProcess.package);
    const selectedPackageCategory: PackageCategory | null | undefined =
        props.packageCategory || useTypedSelector(state => state.bookingState.bookingProcess.filter.category);

    //@ts-ignore
    const minimalDate = selectedPackage === null ? new Date(minimalDatePreset) : packageMinimalDate(selectedPackage);
    const maximumDate = selectedPackage === null ? addMonths(new Date(), 10) : packageMaximumDate(selectedPackage);

    const excludeDaysFromPackage = selectedPackage ? packageExcludeDates(selectedPackage, minimalDate.getMonth()) : [];
    const excludeDaysFromPackageCategory = selectedPackageCategory ? packageCategoryExcludeDates(selectedPackageCategory, minimalDate.getMonth()) : [];
    const excludedDays = getExcludedDays();
    excludeDaysFromPackage.push(...excludedDays);
    excludeDaysFromPackage.push(...excludeDaysFromPackageCategory);
    const excludeDays = excludeDaysFromPackage;

    const startDateFromFilter = () => {
        let startDateFormatted = new Date(Dates.ymd(startDate));
        if (selectedPackage && typeof minimalDate !== null) {
            if ((minimalDate as Date) > startDateFormatted) {
                startDateFormatted = minimalDate as Date;
            }
        }
        const today = new Date();
        if (startDateFormatted > today) {
            return startDateFormatted;
        }
        return today;
    };
    const dateHandler = (date): void => {
        // setStartDate(date);
        props.setValue("date", Dates.ymd(date));
        props.onChange(Dates.ymd(date));
    };

    // @ts-ignore
    const BookingDateInput = forwardRef(({value, onClick}, ref) => (
        // @ts-ignore
        <button ref={ref} className={"grow"} id="ButtonSelectDate" onClick={onClick}>
            <div
                className={clsx(
                    props.disabled ? "disabled bg-primary-800 text-primary-100" : "not-disabled bg-cta-800 text-contrast-800  ",
                    "w-full pointer-events-none px-3 h-10 flex items-center justify-center hover:no-underline text-xl rounded-md"
                )}
            >
                {props.disabled ? t("Select date") : value}
            </div>
        </button>
    ));

    const datePickerLocale = declareLocaleByConfig(LOCALE);
    registerLocale("locale", datePickerLocale);
    setDefaultLocale(datePickerLocale);

    return (
        <div className={"BookingDateFilter flex flex-col relative"}>
            {props.showTitle && (
                <div className={"flex flex-col"}>
                    <CardLabel title={t("Desired date")} />
                </div>
            )}
            {props.package && (
                <div className={"flex items-start"}>
                    <PackageMinDaysFromNow is_minimal={true} package={props.package} />
                </div>
            )}
            <div className={"BookingDateFilterDatePicker mt-4 flex-col w-full flex justify-end z-30"}>
                <DatePicker
                    className="none"
                    customInput={<BookingDateInput />}
                    dateFormat={"dd.MM.yyyy"}
                    excludeDates={excludeDays}
                    locale="locale"
                    maxDate={maximumDate}
                    minDate={minimalDate}
                    popperPlacement={"bottom-end"}
                    selected={startDateFromFilter()}
                    showDisabledMonthNavigation
                    onChange={dateHandler}
                />
                <input {...props.register("date")} name={"date"} type="hidden" />
            </div>
        </div>
    );
};

export default BookingDateFilter;
